import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coachAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';
import CoachLayout from './CoachLayout';
import AccessDenied from '../../components/AccessDenied';

const FAMILIES = ['zone', 'man', 'mixed'];
const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];

function CoverageModal({ domain, coverage, onSave, onClose }) {
  const [form, setForm] = useState({
    display_name: coverage?.display_name || '',
    family: coverage?.family || 'zone',
    difficulty: coverage?.difficulty || 'beginner',
    pre_snap_keys: (coverage?.pre_snap_keys || []).join(', '),
    vulnerable_routes: (coverage?.vulnerable_routes || []).join(', '),
    blitz_tendency: coverage?.blitz_tendency ?? 0.2,
    notes: coverage?.notes || '',
    tags: (coverage?.tags || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.display_name.trim()) { setError('Coverage name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        blitz_tendency: Number(form.blitz_tendency),
        pre_snap_keys: form.pre_snap_keys.split(',').map(s => s.trim()).filter(Boolean),
        vulnerable_routes: form.vulnerable_routes.split(',').map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (coverage) {
        await coachAPI.updateCoverage(domain, coverage.coverage_id, payload);
      } else {
        await coachAPI.createCoverage(domain, payload);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 580, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 className="coach-modal-title">{coverage ? 'Edit Coverage' : 'New Coverage'}</h3>
        {error && <div className="coach-error">⚠ {error}</div>}
        <div className="coach-form-group">
          <label className="coach-label">Coverage Name *</label>
          <input className="coach-input" value={form.display_name} onChange={set('display_name')} placeholder="e.g. Cover 2 — Two Deep Safeties" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="coach-form-group">
            <label className="coach-label">Family</label>
            <select className="coach-select" value={form.family} onChange={set('family')}>
              {FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Difficulty</label>
            <select className="coach-select" value={form.difficulty} onChange={set('difficulty')}>
              {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Blitz Tendency (0–1)</label>
            <input className="coach-input" type="number" min="0" max="1" step="0.05" value={form.blitz_tendency} onChange={set('blitz_tendency')} />
          </div>
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Pre-Snap Keys (comma-separated)</label>
          <input className="coach-input" value={form.pre_snap_keys} onChange={set('pre_snap_keys')} placeholder="safeties_split_wide, corners_pressed, two_lbs_middle" />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Vulnerable Routes (comma-separated)</label>
          <input className="coach-input" value={form.vulnerable_routes} onChange={set('vulnerable_routes')} placeholder="post, seam, corner" />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Coaching Notes</label>
          <textarea className="coach-textarea" value={form.notes} onChange={set('notes')} placeholder="Key identifiers and QB decision points..." />
        </div>
        <div className="coach-form-group">
          <label className="coach-label">Tags (comma-separated)</label>
          <input className="coach-input" value={form.tags} onChange={set('tags')} placeholder="base_defense, common, 2_high" />
        </div>
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : coverage ? 'Update Coverage' : 'Create Coverage'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CoverageLibraryPage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [coverages, setCoverages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCov, setEditingCov] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterFamily) params.family = filterFamily;
    if (filterDiff) params.difficulty = filterDiff;
    coachAPI.listCoverages(domain, params)
      .then(({ data }) => setCoverages(data.coverages || []))
      .catch(e => {
        if (e.response?.status === 403) { setAccessDenied(true); }
        else { setError(e.response?.data?.detail || e.message); }
      })
      .finally(() => setLoading(false));
  }, [domain, filterFamily, filterDiff]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (covId, name) => {
    const ok = await confirm({
      title: 'Delete coverage?',
      message: `Delete coverage "${name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await coachAPI.deleteCoverage(domain, covId).catch(() => {});
    toast.info('Coverage deleted');
    load();
  };

  const filtered = coverages.filter(c =>
    !search || c.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.notes?.toLowerCase().includes(search.toLowerCase())
  );

  if (accessDenied) return <AccessDenied domainId={domain} onBack={() => navigate('/coach')} />;

  return (
    <CoachLayout title={`Coverage Library — ${domain.charAt(0).toUpperCase() + domain.slice(1)}`}>
      {error && <div className="coach-error">⚠ {error}</div>}

      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">{coverages.length} Coverages</h2>
          <button className="btn btn-primary" onClick={() => { setEditingCov(null); setShowModal(true); }}>
            + New Coverage
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input className="coach-input" style={{ flex: '1 1 200px' }} placeholder="Search coverages…" value={search} onChange={e => setSearch(e.target.value)} />
          <select className="coach-select" style={{ flex: '0 0 120px' }} value={filterFamily} onChange={e => setFilterFamily(e.target.value)}>
            <option value="">All families</option>
            {FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select className="coach-select" style={{ flex: '0 0 140px' }} value={filterDiff} onChange={e => setFilterDiff(e.target.value)}>
            <option value="">All difficulties</option>
            {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="coach-loading">Loading coverage library…</div>
        ) : filtered.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">🛡️</span>
            <div className="coach-empty-title">No coverages yet</div>
            <div className="coach-empty-desc">Add defensive coverages that learners will identify during sessions.</div>
          </div>
        ) : (
          <table className="coach-table">
            <thead>
              <tr><th>Coverage</th><th>Family</th><th>Difficulty</th><th>Blitz %</th><th>Pre-Snap Keys</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map(cov => (
                <tr key={cov.coverage_id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{cov.display_name}</div>
                    {cov.notes && <div className="text-muted text-sm">{cov.notes.slice(0, 70)}…</div>}
                  </td>
                  <td><span className={`badge badge-${cov.family === 'man' ? 'published' : 'draft'}`}>{cov.family}</span></td>
                  <td>{cov.difficulty}</td>
                  <td>{Math.round((cov.blitz_tendency || 0) * 100)}%</td>
                  <td className="text-muted text-sm">{(cov.pre_snap_keys || []).slice(0, 2).join(', ')}{cov.pre_snap_keys?.length > 2 ? '…' : ''}</td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-outline btn-sm" onClick={() => { setEditingCov(cov); setShowModal(true); }}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(cov.coverage_id, cov.display_name)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <CoverageModal
          domain={domain}
          coverage={editingCov}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </CoachLayout>
  );
}
